import { appLocalStorage } from '../../../utils/storage';
// Store only the source ID. A quoted author's text and visibility are always
// read afresh, independently of the HTML owned by the person composing.
import { opinionDraftId } from '../../../media/draftIdentity';

export const quoteDraftKey = (userId, storyId) => `comment-quote:${userId}:${storyId}`;

const validId = value => Number.isSafeInteger(value) && value > 0;

export function createQuoteDraft(userId, storyId, request = null) {
  const key = quoteDraftKey(userId, storyId);
  const draftId = userId ? opinionDraftId(userId, storyId) : `guest:${storyId}`;
  let saved;
  if (userId) {
    try {
      const value = JSON.parse(appLocalStorage.getItem(key));
      if (value && (value.id === null || validId(value.id) || (value.id === undefined && typeof value.placementRemoved === 'boolean'))) saved = value;
    } catch { /* A damaged/local-storage-disabled draft must not break editing. */ }
  }
  // Returning to the same history entry keeps an intentional removal. A new
  // repost action is a new selection, even if an older editor draft exists.
  if (request && saved?.requestKey !== request.key) {
    return { key, draftId, id: validId(request.id) ? request.id : undefined,
      invalid: !validId(request.id), requestKey: request.key };
  }
  return { key, draftId, id: saved?.id, requestKey: saved?.requestKey, placementRemoved: Boolean(saved?.placementRemoved), invalid: false };
}

export function persistQuoteDraft(draft) {
  if (draft.id === undefined && draft.placementRemoved === undefined) return;
  try {
    appLocalStorage.setItem(draft.key, JSON.stringify({ id: draft.id, draftId: draft.draftId, requestKey: draft.requestKey, placementRemoved: draft.placementRemoved }));
  } catch { /* Keep this session's attachment usable without local storage. */ }
}

export function detachQuoteDraft(draft) {
  draft.id = null;
  draft.invalid = false;
  persistQuoteDraft(draft);
}

export function clearQuoteDraft(draft) {
  try { appLocalStorage.removeItem(draft.key); } catch { /* Saving already succeeded. */ }
}

export function quoteRequest(location) {
  const query = new URLSearchParams(location.search || '');
  const raw = query.has('quote') ? query.get('quote') : location.state?.quote?.id;
  if (raw === undefined || raw === null) return null;
  // Number('') is zero, so an empty/invalid explicit link cannot silently fall
  // back to an older source and accidentally save the wrong quotation.
  return { id: Number(raw), key: `${location.key || 'default'}:${raw}` };
}

export function sourceQuote(response, id, storyId) {
  const source = response?.list?.find(item => Number(item.id) === id);
  if (!response?.success || !source?.user || source.unavailable || source.privat ||
      Number(source.story?.id ?? source.story_id) !== Number(storyId) ||
      (source.text_status && source.text_status !== 'approved')) {
    throw new Error('Quote unavailable');
  }
  return { id: source.id, story_id: Number(storyId), user: source.user,
    scope: source.scope, text_msg: source.text_msg || '',
    text_msg_translation: source.text_msg_translation || '',
    // Media belongs to this verified source. Keep its DTOs for the quote
    // preview, independently of the composing author's upload attachments.
    media: Array.isArray(source.media) ? source.media : [],
    has_quote: Boolean(source.quoted_reaction_id || source.quote || source.has_quote) };
}
